import axios from 'axios';
import {makeAutoObservable} from 'mobx';
import {api} from '../../../api/endpoints';
import AppStore from '../../../app.store';
import ProductTypeStore from '../product.type.store';
import ProductCategoriesStore from '../product.categories.store';
import ProductBrandsStore from '../product.brands.store';
import {toast} from 'react-toastify';
import {UploadFile} from "antd";
import {ProductModel} from "../products.model";

import {getRequestErrorReason} from '../../../utils/requestError';
import {reconcileServiceFormCapabilities, sanitizeServiceFormForSave} from '../../../utils/serviceCapabilities';

class ProductsCreateEditStore {
  private initialValues: ProductModel = {
    attributes: "",
    has_variation: false,
    last_price: 0,
    show_in_online_shop: false,
    brand: "",
    category: "",
    cost_price: 0,
    goods_id: 0,
    images: undefined,
    profitability: "",
    quantity: 0,
    status: "",
    total_amount: 0,
    type: "",
    type_quantity: 0,
    uid: "",
    url: "",
    id: 0,
    type_id: 0,
    brand_id: 1,
    category_id: 1,
    name: '',
    description: '',
    part_number: '',
    cost: 0,
    set_price: 0,
    fixed_price: false,
    outflow: false,
    outflow_id: 0,
    bonus_percentage: 0,
    // todo remove
    fixed_commission_limit: 0,
    fixed_commission_amount: 0,
    //

    commission_type_id: 0,
    commission: 0,
    fixed_commission: 0,
    commission_limit: 0,
    commission_before_limit: 0,
    commission_after_limit: 0,
    has_expiry: false,
    min: 0,
    max: 0,
    has_serial: false,
    has_stock: true,
    length: 0,
    width: 0,
    height: 0,
    package_weight: 0,
    quantity_in_package: 0,
    debt: true,
    installment: false,
    installment_pre_amount: 0,
    installment_pre_percentage: 0,
    measure_unit_id: 2, // pieces by default
    measure_unit: '',
    cashback: 0,
    cashback_mode: 'common',
    service_profile: 'payment',
    requires_account: true,
    has_commission: true,
    amount_mode: 'manual',
    track_cost: false,
    variations_combinations: [],
  };

  public form: ProductModel = this.initialValues;
  public error = '';
  public message = '';
  public isLoading = false;
  public fileList: UploadFile[] = [];
  public measureUnits: any[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  public init = (initialValues: any) => {
    const { productTypeReq } = ProductTypeStore;
    const { productCategoriesReq } = ProductCategoriesStore;
    const { productBrandReq } = ProductBrandsStore;
    void productTypeReq();
    void productCategoriesReq();
    void productBrandReq();
    if (initialValues) {
      this.form = {
        ...initialValues,
        ...(Number(initialValues.type_id) === 2
          ? {
              service_profile: initialValues.service_profile || 'payment',
              requires_account: initialValues.requires_account ?? true,
              has_commission: initialValues.has_commission ?? true,
              amount_mode: initialValues.amount_mode || 'manual',
              track_cost: initialValues.track_cost ?? false,
            }
          : {}),
      };
      // this.fileList = initialValues.Images;
      if (initialValues.outflow) {
        this.form.outflow_id = 2;
      } else {
        this.form.outflow_id = 1;
      }
    } else {
      this.form = this.initialValues;
    }
  };

  setMeasureUnits(data: any) {
    this.measureUnits = data;
  }

  private getHeaders() {
    const {getHeaders} = AppStore;
    return getHeaders();
  }

  public createUpdateProduct = async (edit?: boolean) => {
    this.isLoading = true;
    try {
      this.form = sanitizeServiceFormForSave({
        ...this.form,
        outflow: this.form.outflow_id === 2,
      });

      const payload = {
        id: this.form.id,
        type_id: this.form.type_id,
        brand_id: this.form.brand_id,
        category_id: this.form.category_id,
        name: this.form.name,
        description: this.form.description,
        part_number: this.form.part_number,
        cost: Number(this.form.cost),
        set_price: Number(this.form.set_price),
        fixed_price: this.form.fixed_price,
        outflow: this.form.outflow,
        outflow_id: this.form.outflow_id,
        bonus_percentage: Number(this.form.bonus_percentage),
        //fixed_commission_limit: Number(this.form.fixed_commission_limit),
        //fixed_commission_amount: Number(this.form.fixed_commission_amount),
        commission_type_id: Number(this.form.commission_type_id),
        fixed_commission: Number(this.form.fixed_commission),
        commission: Number(this.form.commission),
        commission_limit: Number(this.form.commission_limit),
        commission_before_limit: Number(this.form.commission_before_limit),
        commission_after_limit: Number(this.form.commission_after_limit),
        has_expiry: this.form.has_expiry,
        min: Number(this.form.min),
        max: Number(this.form.max),
        has_serial: this.form.has_serial,
        has_stock: this.form.has_stock !== undefined ? this.form.has_stock : true,
        length: Number(this.form.length),
        width: Number(this.form.width),
        height: Number(this.form.height),
        package_weight: Number(this.form.package_weight),
        quantity_in_package: Number(this.form.quantity_in_package),
        debt: this.form.debt,
        installment: this.form.installment,
        installment_pre_amount: Number(this.form.installment_pre_amount),
        installment_pre_percentage: Number(this.form.installment_pre_percentage),
        measure_unit_id: Number(this.form.measure_unit_id),
        cashback: Number(this.form.cashback),
        cashback_mode: this.form.cashback_mode || (Number(this.form.cashback) > 0 ? 'individual' : 'common'),
        service_profile: this.form.service_profile || 'payment',
        requires_account: this.form.requires_account ?? true,
        has_commission: this.form.has_commission ?? true,
        amount_mode: this.form.amount_mode || 'manual',
        track_cost: this.form.track_cost ?? false,
      };

      const headers = this.getHeaders();
      let res: any;
      if (edit) {
        res = await axios.put(api.product, payload, { headers });

        await this.handleUpload(this.form.id)
      } else {
        res = await axios.post(api.product, payload, { headers });
        if (res.status === 201) {
          await this.handleUpload(res.data.product_id)
        }
      }

      this.message = res.data.message;
      toast.success(this.message);

      return res;
    } catch (err: any) {
      const reason = getRequestErrorReason(err);
      if (!reason) return;
      this.error = reason;
      toast.error(this.error);
    } finally {
      this.isLoading = false;
    }
  };

  public setField = (form: ProductModel) => {
    if (Number(form.type_id) === 2) {
      this.form = reconcileServiceFormCapabilities(this.form, form);
      return;
    }
    this.form = form;
  };

  public destroy = () => {
    this.form = this.initialValues;
  };

  public get productTypeOptions() {
    const { productTypes } = ProductTypeStore;

    if (!productTypes) {
      return [];
    }

    return productTypes.map((productType: any) => ({
      label: productType.name,
      value: productType.id,
    }));
  }

  public get productCategoriesOptions() {
    const { productCategories } = ProductCategoriesStore;

    if (!productCategories) {
      return [];
    }

    return productCategories.map((category: any) => ({
      label: category.name,
      value: category.id,
      template_id: category.attribute_template_id,
    }));
  }

  public get productBrandsOptions() {
    const { productBrands } = ProductBrandsStore;

    if (!productBrands) {
      return [];
    }

    return productBrands.map((productType: any) => ({
      label: productType.name,
      value: productType.id,
    }));
  }

  public get productOutflowOptions() {
    return [
      {label: 'Приход', value: 1},
      {label: 'Расход', value: 2},
    ];
  }

  public get productCommissionTypeOptions() {
    return [
      {label: 'Процент от суммы', value: 1},
      {label: 'Фиксированная сумма', value: 2},
      {label: 'Фиксированная сумма -> Лимит -> Процент от суммы', value: 3},
      {label: 'Фиксированная сумма -> Лимит -> Фиксированная сумма', value: 4},
    ];
  }

  public setFileListStore = (list: UploadFile[]) => {
    this.fileList = list;
  }

  public handleUpload = async (product_id: number) => {
    const { getToken } = AppStore;

    if (!this.fileList || this.fileList.length === 0) {
      return;
    }

    // Фильтрация: только те, у кого есть originFileObj (то есть новые, не загруженные)
    const filesToUpload = this.fileList.filter(file => file.originFileObj);

    if (filesToUpload.length === 0) {
      return;
    }

    try {
      const formData = new FormData();

      filesToUpload.forEach((file) => {
        formData.append('files', file.originFileObj!);
      });

      const response = await axios.post(`${api.product_image}/${product_id}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${getToken()!}`,
        },
      });

      console.log('Загрузка успешна:', response.data);

    } catch (error) {
      console.error('Ошибка загрузки:', error);
    }
  };


  public deleteFile = async (file: UploadFile): Promise<void> => {
    const headers = this.getHeaders();

    const response = await axios.delete(api.product_image + '/' + this.form.id + '/delete/' + file.uid, {headers});
    await new Promise((resolve) => setTimeout(resolve, 1000));
  };

  public getMeasureUnitsReq = async (signal?: AbortSignal) => {
    const headers = this.getHeaders();

    try {
      const response = await axios.get(api.get_measurements, {headers, signal})
      this.setMeasureUnits(response?.data)
    } catch (e: any) {

    }
  }
}

export default new ProductsCreateEditStore();
